/**
 * CreateCollectionModal — lets a reader curate their own cover collection.
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useCoverClub } from './context/CoverClubContext';
import CoverArt from './CoverArt';

function ModalBody({ onClose }) {
  const { entries, createCollection } = useCoverClub();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState([]);

  const toggle = (bookId) => {
    setSelected((prev) => (prev.includes(bookId) ? prev.filter((id) => id !== bookId) : [...prev, bookId]));
  };

  const handleCreate = async () => {
    if (!title.trim()) return;
    try {
      await createCollection({ title, description, bookIds: selected });
      toast(`Created “${title.trim()}”`);
      onClose();
    } catch (error) {
      toast(error.message || 'We could not create the collection.');
    }
  };

  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-ink">New Collection</h2>

      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Collection name</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Covers I'd frame" className="mt-1.5 rounded-xl border-line bg-cream" />
      </div>

      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Description <span className="normal-case text-subtle/70">(optional)</span></label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-1.5 rounded-xl border-line bg-cream text-sm" />
      </div>

      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-wide text-subtle">Pick covers ({selected.length} selected)</label>
        <div className="mt-2 grid max-h-64 grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-5">
          {entries.map((entry) => {
            const isSelected = selected.includes(entry.bookId);
            return (
              <button
                key={entry.bookId}
                type="button"
                onClick={() => toggle(entry.bookId)}
                className={cx('relative aspect-[2/3] overflow-hidden rounded-lg ring-2 transition-all', isSelected ? 'ring-coral' : 'ring-transparent hover:ring-line')}
              >
                <CoverArt book={entry.book} style={entry.style} className="h-full w-full" />
                {isSelected && <span className="absolute inset-0 bg-coral/20" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button variant="primary" icon={false} onClick={handleCreate} disabled={!title.trim()} data-testid="cover-club-collection-confirm">
          Create Collection
        </Button>
      </div>
    </div>
  );
}

export default function CreateCollectionModal({ isOpen, onClose }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-h-[88vh] w-[92vw] max-w-lg overflow-y-auto rounded-2xl border-line bg-card p-6 text-ink">
          <DialogTitle className="sr-only">New Collection</DialogTitle>
          <ModalBody onClose={onClose} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="max-h-[92vh] overflow-y-auto border-line bg-card p-5 pb-8 text-ink">
        <DrawerTitle className="sr-only">New Collection</DrawerTitle>
        <ModalBody onClose={onClose} />
      </DrawerContent>
    </Drawer>
  );
}
